/**
 * Helper functions for creating VishMaker payloads
 * These bridge the gap between legacy code and the new gateway_ws system
 * 
 * Simplified payload structure for VishMaker communication.
 * 
 * Structure:
 * {
 *   "v": "1.0",
 *   "id": "uuid",
 *   "ts": "ISO-8601",
 *   "actor": "user|coder|system",
 *   "type": "request|response",
 *   "channel": "health|chat|discovery|build_feature|deploy|logs",
 *   "status": "streaming|completed|error|connected",
 *   "origin": {
 *     "threadId": "thread-id",
 *     "originMessageId": "uuid",
 *     "originActor": "user|coder|system",
 *     "origints": "ISO-8601",
 *     "userId": "optional",
 *     "projectId": "optional",
 *     "page": "optional"
 *   },
 *   "reply": {
 *     "parentMessageId": "uuid",
 *     "parentActor": "user|coder|system",
 *     "parentts": "ISO-8601"
 *   },
 *   "body": {
 *     "kind": "logs|contract|messages|status|error",
 *     "payload": {}
 *   }
 * }
 * 
 * Status meanings:
 *   - streaming: The backend is streaming data to the frontend.
 *   - completed: The backend has completed the request.
 *   - error: The backend has encountered an error.
 *   - connected: The backend is connected to the frontend.
 * 
 * Channel-specific payload patterns:
 * 
 * for discovery:
 *   - type: request, kind: messages, payload: messages
 *   - type: response, kind: messages, payload: messages
 *   - type: response, kind: error, payload: error
 * 
 * for build_feature:
 *   - type: request, kind: contract, payload: requirements
 *   - type: response, kind: status, payload: status
 *   - type: response, kind: error, payload: error
 * 
 * for chat:
 *   - type: request, kind: messages, payload: messages
 *   - type: response, kind: messages, payload: messages
 *   - type: response, kind: error, payload: error
 * 
 * for logs:
 *   - type: log, kind: logs, payload: logs
 *   - type: response, kind: logs, payload: logs
 *   - type: response, kind: error, payload: error
 * 
 * Sample Payload Examples:
 * 
 * 1. Discovery Request:
 * {
 *   "v": "1.0",
 *   "id": "550e8400-e29b-41d4-a716-446655440000",
 *   "ts": "2025-01-20T10:30:00.000Z",
 *   "actor": "user",
 *   "type": "request",
 *   "channel": "discovery",
 *   "status": "connected",
 *   "origin": {
 *     "threadId": "thread_discovery_1705751400000",
 *     "originMessageId": "550e8400-e29b-41d4-a716-446655440000",
 *     "originActor": "user",
 *     "origints": "2025-01-20T10:30:00.000Z",
 *     "userId": "user123",
 *     "projectId": "proj456"
 *   },
 *   "body": {
 *     "kind": "messages",
 *     "payload": {
 *       "message": {
 *         "text": "I want to build a todo app",
 *         "type": "text"
 *       }
 *     }
 *   }
 * }
 * 
 * 2. Build Feature Request:
 * {
 *   "v": "1.0",
 *   "id": "660e8400-e29b-41d4-a716-446655440001",
 *   "ts": "2025-01-20T10:35:00.000Z",
 *   "actor": "user",
 *   "type": "request",
 *   "channel": "build_feature",
 *   "status": "connected",
 *   "origin": {
 *     "threadId": "thread_build_feature_1705751700000",
 *     "originMessageId": "660e8400-e29b-41d4-a716-446655440001",
 *     "originActor": "user",
 *     "origints": "2025-01-20T10:35:00.000Z",
 *     "userId": "user123",
 *     "projectId": "proj456"
 *   },
 *   "body": {
 *     "kind": "contract",
 *     "payload": {
 *       "metadata": {},
 *       "settings": {},
 *       "requirements": {
 *         "description": "Build a todo app"
 *       }
 *     }
 *   }
 * }
 * 
 * 3. Chat Request with Reply:
 * {
 *   "v": "1.0",
 *   "id": "770e8400-e29b-41d4-a716-446655440002",
 *   "ts": "2025-01-20T10:40:00.000Z",
 *   "actor": "user",
 *   "type": "request",
 *   "channel": "chat",
 *   "status": "connected",
 *   "origin": {
 *     "threadId": "thread_chat_1705752000000",
 *     "originMessageId": "770e8400-e29b-41d4-a716-446655440002",
 *     "originActor": "user",
 *     "origints": "2025-01-20T10:40:00.000Z"
 *   },
 *   "reply": {
 *     "parentMessageId": "550e8400-e29b-41d4-a716-446655440000",
 *     "parentActor": "coder",
 *     "parentts": "2025-01-20T10:35:00.000Z"
 *   },
 *   "body": {
 *     "kind": "messages",
 *     "payload": {
 *       "message": {
 *         "text": "Can you explain that further?",
 *         "type": "text"
 *       }
 *     }
 *   }
 * }
 * 
 * 4. Response (from backend):
 * {
 *   "v": "1.0",
 *   "id": "880e8400-e29b-41d4-a716-446655440003",
 *   "ts": "2025-01-20T10:40:05.000Z",
 *   "actor": "coder",
 *   "type": "response",
 *   "channel": "chat",
 *   "status": "completed",
 *   "origin": {
 *     "threadId": "thread_chat_1705752000000",
 *     "originMessageId": "770e8400-e29b-41d4-a716-446655440002",
 *     "originActor": "user",
 *     "origints": "2025-01-20T10:40:00.000Z"
 *   },
 *   "reply": {
 *     "parentMessageId": "770e8400-e29b-41d4-a716-446655440002",
 *     "parentActor": "user",
 *     "parentts": "2025-01-20T10:40:00.000Z"
 *   },
 *   "body": {
 *     "kind": "messages",
 *     "payload": {
 *       "message": {
 *         "text": "Sure! Here's a detailed explanation...",
 *         "type": "text"
 *       }
 *     }
 *   }
 * }
 */

import { VishMakerPayload, Channel, Actor, Status, BodyKind, Origin, MessageType, Reply } from './outbound';

/**
 * Create a VishMaker payload (for use with WebSocketRouter.sendRequest or direct sending)
 * This replaces the deprecated commManager.createVishmakerPayload
 */
export function createVishmakerPayload(
    type: string, // e.g., 'build_feature', 'discovery', 'deploy'
    actor: Actor,
    payload: Record<string, any>,
    channel?: Channel,
    status?: Status,
    options?: {
        userId?: string;
        projectId?: string;
        page?: string;
        threadId?: string;
        reply?: {
            parentMessageId: string;
            parentActor: Actor;
            parentts: string;
        };
    }
): VishMakerPayload {
    const messageId = crypto.randomUUID();
    const timestamp = new Date().toISOString();
    const threadId = options?.threadId || `thread_${type}_${Date.now()}`;

    // Determine channel from type if not provided
    let finalChannel: Channel = channel || 'build_feature';
    if (type === 'discovery') {
        finalChannel = 'discovery';
    } else if (type === 'build_feature') {
        finalChannel = 'build_feature';
    } else if (type === 'build_epic') {
        finalChannel = 'build_epic';
    } else if (type === 'deploy_feature' || type === 'deploy') {
        finalChannel = 'deploy';
    }

    // Determine body kind from type
    let bodyKind: BodyKind = 'messages';
    if (type === 'build_feature' || type === 'build_epic') {
        bodyKind = 'contract';
    } else if (type === 'deploy_feature' || type === 'deploy') {
        bodyKind = 'status';
    }

    const origin: Origin = {
        threadId,
        originMessageId: messageId,
        originActor: actor,
        origints: timestamp,
        ...(options?.userId && { userId: options.userId }),
        ...(options?.projectId && { projectId: options.projectId }),
        ...(options?.page && { page: options.page })
    };

    // Build the payload structure matching backend exactly
    const vishmakerPayload: VishMakerPayload = {
        v: '1.0',
        id: messageId,
        ts: timestamp,
        actor,
        type: 'request', // Always 'request' when sending from frontend
        channel: finalChannel,
        status: status || 'connected',
        origin,
        body: {
            kind: bodyKind,
            payload: payload
        }
    };

    // Add reply field if provided
    if (options?.reply) {
        vishmakerPayload.reply = {
            parentMessageId: options.reply.parentMessageId,
            parentActor: options.reply.parentActor,
            parentts: options.reply.parentts
        };
    }

    return vishmakerPayload;
}

/**
 * Parse a response from VishCoder (replaces deprecated commManager.parseResponse)
 * This handles both the new VishMakerPayload format and legacy formats
 */
export function parseResponse(data: any): {
    type: string;
    actor: string;
    status: string;
    body: any;
    messageId?: string;
} | null {
    // If it's already a VishMakerPayload, extract the relevant fields
    if (data && typeof data === 'object') {
        // Check for new format
        if (data.v && data.channel && data.body) {
            return {
                type: data.body.kind || data.type || 'unknown',
                actor: data.actor || 'Unknown',
                status: data.status || 'unknown',
                body: data.body.payload || data.body,
                messageId: data.id
            };
        }

        // Check for legacy format with type field
        if (data.type) {
            return {
                type: data.type,
                actor: data.actor || data.source || 'Unknown',
                status: data.status || 'unknown',
                body: data.body || data.messages || data,
                messageId: data.messageId || data.id
            };
        }

        // Check for legacy format with schema_version
        if (data.schema_version && data.message) {
            return {
                type: data.message.type || 'unknown',
                actor: data.message.actor || 'Unknown',
                status: 'completed',
                body: { message: data.message },
                messageId: data.message.message_id
            };
        }
    }

    return null;
}

/**
 * Get WebSocket URL based on environment
 */
export function getWebSocketUrl(): string {
    const environment = import.meta.env.VITE_ENVIRONMENT || 'prod';
    const baseUrl = environment === 'prod' 
        ? 'wss://vishmaker.com' 
        : `wss://${environment}.vishmaker.com`;
    return `${baseUrl}/ws/connect`;
}

/**
 * VishMakerPayload class for creating and managing payloads
 * Similar to the Python VishMakerPayload class, provides a structured way to create payloads
 * 
 * @example
 * ```typescript
 * const payload = new VishMakerPayloadBuilder()
 *   .setActor('user')
 *   .setType('request')
 *   .setChannel('chat')
 *   .setStatus('connected')
 *   .setOrigin({
 *     threadId: 'thread_123',
 *     originMessageId: 'msg_456',
 *     originActor: 'user',
 *     origints: new Date().toISOString()
 *   })
 *   .setBody('messages', { message: { text: 'Hello' } })
 *   .build();
 * ```
 */
export class VishMakerPayloadBuilder {
    private v: string = '1.0';
    private id: string;
    private ts: string;
    private actor?: Actor;
    private type?: MessageType;
    private channel?: Channel;
    private status?: Status;
    private origin?: Origin;
    private reply?: Reply;
    private body?: { kind: BodyKind; payload: Record<string, any> };

    constructor() {
        this.id = crypto.randomUUID();
        this.ts = new Date().toISOString();
    }

    setActor(actor: Actor): this {
        this.actor = actor;
        return this;
    }

    setType(type: MessageType): this {
        this.type = type;
        return this;
    }

    setChannel(channel: Channel): this {
        this.channel = channel;
        return this;
    }

    setStatus(status: Status): this {
        this.status = status;
        return this;
    }

    setOrigin(origin: Origin): this {
        this.origin = origin;
        return this;
    }

    setReply(reply: Reply): this {
        this.reply = reply;
        return this;
    }

    setBody(kind: BodyKind, payload: Record<string, any>): this {
        this.body = { kind, payload };
        return this;
    }

    /**
     * Build the final VishMakerPayload
     * Throws an error if required fields are missing
     */
    build(): VishMakerPayload {
        if (!this.actor) {
            throw new Error('Actor is required');
        }
        if (!this.type) {
            throw new Error('Type is required');
        }
        if (!this.channel) {
            throw new Error('Channel is required');
        }
        if (!this.status) {
            throw new Error('Status is required');
        }
        if (!this.origin) {
            throw new Error('Origin is required');
        }
        if (!this.body) {
            throw new Error('Body is required');
        }

        const payload: VishMakerPayload = {
            v: this.v,
            id: this.id,
            ts: this.ts,
            actor: this.actor,
            type: this.type,
            channel: this.channel,
            status: this.status,
            origin: this.origin,
            body: this.body
        };

        if (this.reply) {
            payload.reply = this.reply;
        }

        return payload;
    }

    /**
     * Convert to dictionary for JSON serialization
     */
    toDict(): Record<string, any> {
        return this.build();
    }

    /**
     * Convert to JSON string for efficient serialization
     */
    toJSON(): string {
        return JSON.stringify(this.build());
    }

    /**
     * Create a builder from an existing payload
     */
    static fromPayload(payload: VishMakerPayload): VishMakerPayloadBuilder {
        const builder = new VishMakerPayloadBuilder();
        builder.v = payload.v;
        builder.id = payload.id;
        builder.ts = payload.ts;
        builder.actor = payload.actor;
        builder.type = payload.type;
        builder.channel = payload.channel;
        builder.status = payload.status;
        builder.origin = payload.origin;
        builder.reply = payload.reply;
        builder.body = payload.body;
        return builder;
    }
}
